import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { permissionService } from "../services/permissionService";

const AdminPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadRoles();
    loadPermissions();
  }, []);

  useEffect(() => {
    if (selectedRoleId) {
      loadRolePermissions(selectedRoleId);
    } else {
      setSelectedPermissions([]);
    }
  }, [selectedRoleId]);

  const loadRoles = async () => {
    try {
      const response = await permissionService.getRoles();
      setRoles(response.data.data);
    } catch (error) {
      console.error("Error loading roles:", error);
      setMessage("Error loading roles");
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await permissionService.getPermissions();
      setPermissions(response.data.data);
    } catch (error) {
      console.error("Error loading permissions:", error);
      setMessage("Error loading permissions");
    }
  };

  const loadRolePermissions = async (roleId) => {
    setLoading(true);
    try {
      const response = await permissionService.getRolePermissions(roleId);
      const selected = response.data.data
        .filter((p) => p.has_permission === 1)
        .map((p) => p.id);
      setSelectedPermissions(selected);
      setMessage("");
    } catch (error) {
      console.error("Error loading role permissions:", error);
      setMessage("Error loading role permissions");
    }
    setLoading(false);
  };

  const togglePermission = (permissionId) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(permissionId)) {
        return prev.filter((id) => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  const handleSave = async () => {
    if (!selectedRoleId) {
      setMessage("Please select a role first");
      return;
    }

    setSaving(true);
    setMessage("");
    try {
      await permissionService.updateRolePermissions(selectedRoleId, selectedPermissions);
      setMessage("Permissions updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving permissions:", error);
      setMessage(error.response?.data?.message || "Error saving permissions");
    }
    setSaving(false);
  };

  return (
    <Layout title="Set Permissions">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Role Permissions Management</h2>

        {message && (
          <div
            className={`mb-4 p-4 rounded-md ${
              message.includes("Error")
                ? "bg-red-100 text-red-700"
                : "bg-blue-100 text-blue-700"
            }`}
          >
            {message}
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Role
          </label>
          <select
            value={selectedRoleId}
            onChange={(e) => setSelectedRoleId(e.target.value)}
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">-- Select Role --</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.role_name}
              </option>
            ))}
          </select>
        </div>

        {selectedRoleId && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">
              Permissions for {roles.find((r) => r.id === parseInt(selectedRoleId))?.role_name}
            </h3>

            {loading ? (
              <div className="text-center py-8">Loading permissions...</div>
            ) : (
              <>
                <div className="space-y-3 mb-6">
                  {permissions.map((permission) => (
                    <label
                      key={permission.id}
                      className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-md cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission.id)}
                        onChange={() => togglePermission(permission.id)}
                        className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {permission.permission_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {permission.permission_key}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? "Saving..." : "Save Permissions"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {!selectedRoleId && (
          <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
            Please select a role to manage its permissions
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminPermissions;

